import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTheme } from "@mui/material";
import React, { useEffect, useCallback, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { Rectangle } from "../interfaces";
import { DeleteButton, RectangleContentContainer, TitleContainer } from "./styles";

interface Props {
  pageNumber: number;
  rectangles: Rectangle[];
  handleDeleteRectangle?: (rectangleId: string) => void;
  handleDrawnRectangle?: (rectangle: Rectangle) => { isAccepted: boolean } | undefined;
  handleClickRectangle?: (rectangleId: string) => void;
  allowNestedRectangles?: boolean;
}

const OverlayingCanvas = ({
  pageNumber,
  rectangles,
  handleDeleteRectangle,
  handleDrawnRectangle,
  handleClickRectangle,
  allowNestedRectangles = true,
}: Props) => {
  const theme = useTheme();
  const overLayingCanvasRef = useRef<HTMLCanvasElement>(null);
  const overLayingCanvasRefMarginLeft = useRef<string>("");
  const [overlayMarginLeft, setOverlayMarginLeft] = useState<string>("");

  const startRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const isSelectingRef = useRef(false);

  // #region drawing direction
  /**
   *  @return "LRTB" : left to right + top to bottom |
   *          "LRBT": left to right + bottom to top   |
   *          "RLTB" : right to left + top to bottom |
   *          "RLBT" : right to left + bottom to top
   */
  const drawingDirection = useCallback((x: number, y: number): "LRTB" | "LRBT" | "RLTB" | "RLBT" => {
    //left to right / top to down
    if (x > startRef.current.x && y > startRef.current.y) {
      return "LRTB";
    } else if (x < startRef.current.x && y > startRef.current.y) {
      return "RLTB";
    } else if (x > startRef.current.x && y < startRef.current.y) {
      return "LRBT";
    } else if (x < startRef.current.x && y < startRef.current.y) {
      return "RLBT";
    }

    return "LRTB";
  }, []);
  // #endregion

  // #region clear rectangle
  const cleaRRectangle = useCallback((x: number, y: number, width: number, height: number) => {
    const ctx = overLayingCanvasRef.current?.getContext("2d");
    if (overLayingCanvasRef.current && ctx) {
      if (overLayingCanvasRef.current && ctx) {
        ctx.clearRect(x - 2, y - 2, width + 4, height + 4);
      }
    }
  }, []);
  // #endregion

  // #region prehandle drawn rectangle
  const preHandleDrawnRectangle = useCallback(
    (width: number, height: number) => {
      console.log("preHandle Drawn Rectangle on page ", pageNumber);
      const { x, y } = startRef.current;

      if (handleDrawnRectangle) {
        const parentFeedback = handleDrawnRectangle({ id: uuid(), pageNumber, x, y, width, height, number: 0 });
        if (parentFeedback?.isAccepted === false) {
          cleaRRectangle(x, y, width, height);
        }
      }
    },
    [handleDrawnRectangle, pageNumber]
  );
  // #endregion

  // #rerion get canvas coords
  const getCanvasCoords = (event: MouseEvent) => {
    let consideredX: number;
    let consideredY: number;

    const mouseX = event.clientX;
    const mouseY = event.clientY;

    if (overLayingCanvasRef.current) {
      const {
        right: canvasRight,
        left: canvasLeft,
        bottom: canvasBottom,
        top: canvasTop,
      } = overLayingCanvasRef.current.getBoundingClientRect();

      if (event.type === "mouseleave") {
        if (mouseX > canvasRight) {
          consideredX = canvasRight - canvasLeft;
        } else if (mouseX < canvasLeft) {
          consideredX = 1;
        } else {
          consideredX = mouseX - canvasLeft;
        }

        if (mouseY > canvasBottom) {
          consideredY = canvasBottom - canvasTop;
        } else if (mouseY < canvasTop) {
          consideredY = 1;
        } else {
          consideredY = mouseY - canvasTop;
        }
      } else {
        consideredX = mouseX - canvasLeft;
        consideredY = mouseY - canvasTop;
      }

      return {
        x: consideredX,
        y: consideredY,
      };
    }

    return {
      x: 0,
      y: 0,
    };
  };
  // #endregion

  // #region is inside another rectangle
  const isInsideRectangle = useCallback(
    (e: MouseEvent): Rectangle | null => {
      const { x, y } = getCanvasCoords(e);
      // console.log(rectangles);

      const clickedRectangle = rectangles.find((r) => {
        const minX = r.x;
        const maxX = r.x + r.width;

        const minY = r.y;
        const maxY = r.y + r.height;

        const withinXRange = x === minX || x === maxX || (x > minX && x < maxX);
        const withinYRange = y === minY || y === maxY || (y > minY && y < maxY);

        return r.pageNumber === pageNumber && withinXRange && withinYRange;
      });

      return clickedRectangle ?? null;
    },
    [pageNumber, rectangles]
  );
  // #endregion

  // #region on mouse down
  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      console.log("mouse down");
      const { x, y } = getCanvasCoords(e);

      if (!allowNestedRectangles) {
        const insideAnotherRectangle = !!isInsideRectangle(e);
        if (!insideAnotherRectangle) {
          startRef.current = { x, y };
          isSelectingRef.current = true;
        }
      } else {
        startRef.current = { x, y };
        isSelectingRef.current = true;
      }
    },
    [isInsideRectangle, rectangles, allowNestedRectangles]
  );
  // #endregion

  // #region on mouse move
  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isSelectingRef.current) return;
      console.log("mouse move");
      const ctx = overLayingCanvasRef.current?.getContext("2d");
      if (overLayingCanvasRef.current && ctx) {
        const { x, y } = getCanvasCoords(e);
        const drawingdirectionResult = drawingDirection(x, y);

        switch (drawingdirectionResult) {
          case "LRTB": {
            ctx.clearRect(
              startRef.current.x - 1,
              startRef.current.y - 1,
              x + 20 - startRef.current.x,
              y + 20 - startRef.current.y
            );
            break;
          }
          case "RLTB": {
            ctx.clearRect(x - 10, y - 10, startRef.current.x - x + 10, startRef.current.y - y + 10);
            break;
          }
          case "LRBT": {
            ctx.clearRect(startRef.current.x - 1, y - 10, x - startRef.current.x + 20, startRef.current.y - y + 20);
            break;
          }
          case "RLBT": {
            ctx.clearRect(x - 10, y - 10, startRef.current.x - x + 20, startRef.current.y - y + 20);
            break;
          }
          default:
            break;
        }

        currentRef.current = { x, y };
        ctx.beginPath();
        ctx.strokeStyle = `${theme.palette.primary.main}`;
        ctx.strokeRect(startRef.current.x, startRef.current.y, x - startRef.current.x, y - startRef.current.y);

        if (!allowNestedRectangles) {
          const isInsideOtherRactangle = isInsideRectangle(e);
          if (isInsideOtherRactangle) {
            onMouseUpORLeave(e);
          }
        }
      }
    },
    [drawingDirection, isInsideRectangle, allowNestedRectangles]
  );
  // #endregion

  // #region on mouse up
  const onMouseUpORLeave = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      if (!isSelectingRef.current) return;
      isSelectingRef.current = false;

      let x: number, y: number;

      if (e instanceof MouseEvent) {
        const { x: canvasCordsX, y: canvasCordsY } = getCanvasCoords(e);
        x = canvasCordsX;
        y = canvasCordsY;
      } else {
        const { x: canvasCordsX, y: canvasCordsY } = getCanvasCoords(e.nativeEvent);
        x = canvasCordsX;
        y = canvasCordsY;
      }

      const width = Math.abs(x - startRef.current.x);
      const height = Math.abs(y - startRef.current.y);

      if (width > 1) {
        const drawingdirectionResult = drawingDirection(x, y);

        switch (drawingdirectionResult) {
          case "RLTB": {
            startRef.current.x = x;
            break;
          }
          case "LRBT": {
            startRef.current.y = y;
            break;
          }
          case "RLBT": {
            startRef.current.x = x;
            startRef.current.y = y;
            break;
          }
          default:
            break;
        }
        preHandleDrawnRectangle(width, height);
      }
    },
    [preHandleDrawnRectangle, pageNumber, drawingDirection]
  );
  // #endregion

  // #region detect canvas click
  const onCanvasClick = useCallback(
    (e: MouseEvent) => {
      const clickedRectangle = isInsideRectangle(e);
      if (clickedRectangle) {
        console.log("Clicked rectangle:", clickedRectangle);
        handleClickRectangle && handleClickRectangle(clickedRectangle.id);
      }
    },
    [handleClickRectangle, isInsideRectangle]
  );
  // #endregion

  // #region prehandle delete rectangle
  const prehandleDeleteRectangle = useCallback(
    (rectangleId: string) => {
      const rectangle = rectangles.find((rectangle) => rectangle.id === rectangleId);

      if (rectangle) {
        const ctx = overLayingCanvasRef.current?.getContext("2d");
        if (overLayingCanvasRef.current && ctx) {
          ctx.clearRect(rectangle.x - 2, rectangle.y - 2, rectangle.width + 4, rectangle.height + 4);
          handleDeleteRectangle && handleDeleteRectangle(rectangle.id);
        }
      }
    },
    [rectangles, handleDeleteRectangle]
  );
  // #endregion

  // #region init event listeners
  useEffect(() => {
    const overlayingCanvas = overLayingCanvasRef.current;

    if (handleClickRectangle) {
      overlayingCanvas?.addEventListener("click", onCanvasClick);

      return () => {
        overlayingCanvas?.removeEventListener("click", onCanvasClick);
      };
    }

    if (handleDrawnRectangle) {
      overlayingCanvas?.addEventListener("mousedown", onMouseDown);
      overlayingCanvas?.addEventListener("mousemove", onMouseMove);
      overlayingCanvas?.addEventListener("mouseup", onMouseUpORLeave);
      overlayingCanvas?.addEventListener("mouseleave", onMouseUpORLeave);

      return () => {
        overlayingCanvas?.removeEventListener("mousedown", onMouseDown);
        overlayingCanvas?.removeEventListener("mousemove", onMouseMove);
        overlayingCanvas?.removeEventListener("mouseup", onMouseUpORLeave);
        overlayingCanvas?.removeEventListener("mouseleave", onMouseUpORLeave);
      };
    }
  }, [pageNumber, handleDrawnRectangle, handleClickRectangle, rectangles]);
  // #endregion

  // #region draw existing rectangles
  const drawExistingRectangles = useCallback(() => {
    console.log("drawing exisiting rectangles");
    const ctx = overLayingCanvasRef.current?.getContext("2d");
    if (overLayingCanvasRef.current && ctx) {
      ctx.clearRect(0, 0, overLayingCanvasRef.current.width, overLayingCanvasRef.current.height);

      rectangles
        .filter((_rectangle) => _rectangle.pageNumber === pageNumber)
        .forEach((_rectangle) => {
          // console.log("drawing rectangle", _rectangle);
          ctx.beginPath();
          ctx.strokeStyle = `${theme.palette.primary.main}`;
          ctx.strokeRect(_rectangle.x, _rectangle.y, _rectangle.width, _rectangle.height);
        });
    }
  }, [pageNumber, rectangles]);
  // #endregion

  useEffect(() => {
    drawExistingRectangles();
  }, [pageNumber]);

  // #region resize according to main-viewer
  useEffect(() => {
    console.log("resizing");
    const mainViewer = document.getElementById("main-viewer");
    const container = document.getElementById("document-viewer-container");

    if (mainViewer && container) {
      const containerObserver = new ResizeObserver(([entry]) => {
        if (overLayingCanvasRef.current) {
          const computed = getComputedStyle(mainViewer);
          const computedMarginLeft = computed.getPropertyValue("margin-left");

          overLayingCanvasRefMarginLeft.current = `${computedMarginLeft}`;
          overLayingCanvasRef.current.style.marginLeft = `${computedMarginLeft}`;
          // update state so React re-renders the positioned elements
          setOverlayMarginLeft(`${computedMarginLeft}`);

          drawExistingRectangles();
        }
      });

      const mainViewerObserver = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        if (overLayingCanvasRef.current) {
          const canvas = overLayingCanvasRef.current;
          const computed = getComputedStyle(mainViewer);
          canvas.width = width;
          canvas.height = height;

          overLayingCanvasRef.current.style.width = `${width}px`;
          overLayingCanvasRef.current.style.height = `${height}px`;

          const computedMarginLeft = computed.getPropertyValue("margin-left");

          overLayingCanvasRefMarginLeft.current = `${computedMarginLeft}`;
          overLayingCanvasRef.current.style.marginLeft = `${computedMarginLeft}`;
          // update state so React re-renders the positioned elements
          setOverlayMarginLeft(`${computedMarginLeft}`);

          drawExistingRectangles();
        }
      });

      containerObserver.observe(container);
      mainViewerObserver.observe(mainViewer);

      // cleanup observers on unmount or when dependencies change
      return () => {
        try {
          containerObserver.disconnect();
        } catch (e) {
          console.error(e as string);
        }
        try {
          mainViewerObserver.disconnect();
        } catch (e) {
          console.error(e as string);
        }
      };
    }
  }, [drawExistingRectangles]);
  // #endregion

  return (
    <>
      <canvas
        id="main-viewer-overlaying"
        ref={overLayingCanvasRef}
        style={{
          position: "absolute",
          // , left: "28.9%"
        }}
      />
      {rectangles
        .filter((_rectangle) => _rectangle.pageNumber === pageNumber)
        .map((_rectangle, idx) => (
          <React.Fragment key={idx}>
            {handleDeleteRectangle && _rectangle.isDeletable ? (
              <DeleteButton
                top={_rectangle.y - 25}
                marginLeft={`calc( ${overlayMarginLeft || overLayingCanvasRefMarginLeft.current} + ${
                  _rectangle.x + _rectangle.width - 25
                }px)`}
                onClick={() => prehandleDeleteRectangle(_rectangle.id)}
              >
                <FontAwesomeIcon
                  icon={faXmark}
                  color={theme.palette.error.main}
                />
              </DeleteButton>
            ) : (
              <></>
            )}
            {_rectangle.content ? (
              <RectangleContentContainer
                marginLeft={`calc( ${overlayMarginLeft || overLayingCanvasRefMarginLeft.current} + ${
                  _rectangle.x + 2
                }px)`}
                top={_rectangle.y}
                width={_rectangle.width - 4}
                height={_rectangle.height - 4}
                onClick={(e) => onCanvasClick(e.nativeEvent)}
              >
                {_rectangle.content}
              </RectangleContentContainer>
            ) : (
              <></>
            )}
            {_rectangle.title ? (
              <TitleContainer
                top={_rectangle.y - 24}
                marginLeft={`calc( ${overlayMarginLeft || overLayingCanvasRefMarginLeft.current} + ${
                  _rectangle.x - 1
                }px)`}
              >
                {_rectangle.title}
              </TitleContainer>
            ) : (
              <></>
            )}
          </React.Fragment>
        ))}
    </>
  );
};

export default OverlayingCanvas;
